"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import type { ScheduledTask } from "@/lib/study-plan-state";
import { taskSortValue } from "@/lib/study-plan-state";

/**
 * 학습 일정 캘린더 DnD 공용 헬퍼 (JCG-2310).
 *
 * 이 환경에서 dnd-kit `useDroppable` 측정이 동작하지 않아(e.over 가 안 잡힘) droppable 을 버리고
 * **커서 좌표(activator+delta) → document.elementsFromPoint → [data-day-column]/[data-task-uid]**
 * 로 드롭 대상을 직접 찾는다. 주 뷰(WeekBoard/ScheduleCalendar)와 일자 상세 모달(DayDetailDialog)이
 * 모두 같은 마커 규약을 쓰면 이 모듈을 그대로 공유한다.
 *
 * 마커 규약:
 * - 드롭 칼럼 컨테이너에 `data-day-column={dateISO}`
 * - 각 task 카드(TaskCard)에 `data-task-uid={task.uid}` (드래그 중엔 pointer-events-none 으로 투과)
 */

/** 드래그 중 드롭 위치 — 놓일 칼럼 날짜 + 삽입 기준 카드(없으면 칼럼 끝) + 그 카드 위/아래. */
export interface DropIndicator {
  toISO: string;
  refUid: string | null;
  after: boolean;
}

/** activator(드래그 시작 포인터) + delta = 현재 커서 위치. 포인터 이벤트가 아니면 null. */
export function pointerXY(
  activator: Event | null,
  delta: { x: number; y: number },
): { x: number; y: number } | null {
  if (!activator || !("clientX" in activator)) return null;
  const me = activator as MouseEvent;
  return { x: me.clientX + delta.x, y: me.clientY + delta.y };
}

/** 드래그 중 칼럼 1개의 고정 스냅샷: 칼럼 가로/세로 범위 + 카드 세로 중점. dropTargetFrozen 이 소비. */
export interface FrozenColumn {
  left: number;
  right: number;
  top: number;
  bottom: number;
  cards: { uid: string; mid: number }[];
}

/** 드래그 중 칼럼별 고정 스냅샷. */
export type FrozenColumns = Map<string, FrozenColumn>;

/**
 * 드래그 시작 시 각 칼럼([data-day-column])의 카드([data-task-uid]) 세로 중점을 스냅샷한다(뷰포트 기준).
 * 드래그 중 삽입 인디케이터가 레이아웃을 밀어 elementsFromPoint 결과가 흔들리는(jitter) 걸 막기 위해,
 * 칼럼 내 삽입 위치는 이 "고정" 중점으로 계산한다(드래그 시작 시점엔 인디케이터가 아직 없어 깨끗한 위치).
 */
export function freezeColumns(): FrozenColumns {
  const map: FrozenColumns = new Map();
  for (const col of document.querySelectorAll<HTMLElement>(
    "[data-day-column]",
  )) {
    const iso = col.getAttribute("data-day-column");
    if (!iso) continue;
    // 칼럼 슬라이드 애니메이션(translateX)을 잠깐 빼고 레이아웃 x 범위를 잰다 → 슬라이드 중에도 칼럼 판정 안정.
    const t = col.style.transform;
    col.style.transform = "";
    const cr = col.getBoundingClientRect();
    const cards = Array.from(
      col.querySelectorAll<HTMLElement>("[data-task-uid]"),
    ).map((c) => {
      const r = c.getBoundingClientRect();
      return {
        uid: c.getAttribute("data-task-uid") ?? "",
        mid: r.top + r.height / 2,
      };
    });
    col.style.transform = t;
    map.set(iso, {
      left: cr.left,
      right: cr.right,
      top: cr.top,
      bottom: cr.bottom,
      cards,
    });
  }
  return map;
}

/**
 * 고정 스냅샷(freezeColumns) 기준 드롭 위치.
 * - 커서: 호출부가 직접 뷰포트 좌표를 넘긴다(주 뷰=자체 pointer 추적, 모달=pointerXY(activator,delta)).
 *   dnd-kit 수명주기와 분리해 ±4일 너머 드래그를 지원하기 위해 시그니처를 커서 직접 받기로 바꿨다(JCG-2458).
 * - 놓인 칼럼: 고정 x 범위 기준 커서에 가장 가까운 칼럼(슬라이드 중에도 안정)으로 toISO 판정.
 * - 칼럼 내 위치: 고정 중점 vs 커서 Y — 첫 "중점이 커서보다 아래"인 카드 앞에 삽입(없으면 칼럼 끝).
 *   고정값이라 인디케이터가 카드를 밀어도 결과가 단조롭고 안정적이다(jitter 없음).
 */
export function dropTargetFrozen(
  cursor: { x: number; y: number } | null,
  frozen: FrozenColumns,
  activeUid: string,
): DropIndicator | null {
  const p = cursor;
  if (!p) return null;
  // 칼럼 판정: 고정 x 범위 기준 커서에 가장 가까운 칼럼(슬라이드 중에도 안정 — elementsFromPoint 안 씀).
  let toISO: string | null = null;
  let chosen: FrozenColumn | null = null;
  let best = Infinity;
  for (const [iso, col] of frozen) {
    const center = (col.left + col.right) / 2;
    const d = Math.abs(p.x - center);
    if (d < best) {
      best = d;
      toISO = iso;
      chosen = col;
    }
  }
  if (!toISO || !chosen) return null;
  // 커서가 가장 가까운 칼럼에서 가로로 1칸 폭, 세로로 1칸 높이 넘게 벗어나면(=보드 옆·위·아래 여백에 떨굼)
  // 드롭을 취소한다 — 취소하려고 멀리 던진 카드가 가장자리 날짜로 잘못 이동하는 것 방지(JCG-2310 리뷰).
  const colW = chosen.right - chosen.left;
  const colH = chosen.bottom - chosen.top;
  if (p.x < chosen.left - colW || p.x > chosen.right + colW) return null;
  if (p.y < chosen.top - colH || p.y > chosen.bottom + colH) return null;
  const sib = chosen.cards.filter((c) => c.uid !== activeUid);
  for (const c of sib) {
    if (p.y < c.mid) return { toISO, refUid: c.uid, after: false };
  }
  return { toISO, refUid: null, after: true };
}

/** 이웃 두 task 사이(또는 끝)의 새 정렬값 — 분수 인덱싱. computeDropOrder 내부 전용. */
function orderBetween(
  prev: ScheduledTask | null,
  next: ScheduledTask | null,
  self: ScheduledTask,
): number {
  const p = prev ? taskSortValue(prev) : null;
  const n = next ? taskSortValue(next) : null;
  if (p !== null && n !== null) return (p + n) / 2;
  if (p !== null) return p + 1;
  if (n !== null) return n - 1;
  return taskSortValue(self); // 빈 칼럼: 자기 기본값 유지
}

/**
 * 드롭 대상(refUid/after)을 받아, 놓인 칼럼의 정렬된 형제(자기 제외) 기준 앞/뒤 이웃을 구해
 * 새 정렬값을 계산한다. 주 뷰·모달의 handleDragEnd 공용.
 */
export function computeDropOrder(
  target: DropIndicator,
  siblings: ScheduledTask[],
  self: ScheduledTask,
): number {
  const sorted = [...siblings].sort((a, b) => taskSortValue(a) - taskSortValue(b));
  let prevT: ScheduledTask | null = null;
  let nextT: ScheduledTask | null = null;
  if (target.refUid === null) {
    prevT = sorted[sorted.length - 1] ?? null; // 빈 영역 → 끝에 추가
  } else {
    const i = sorted.findIndex((t) => t.uid === target.refUid);
    if (i < 0) {
      prevT = sorted[sorted.length - 1] ?? null;
    } else if (target.after) {
      prevT = sorted[i];
      nextT = sorted[i + 1] ?? null;
    } else {
      prevT = sorted[i - 1] ?? null;
      nextT = sorted[i];
    }
  }
  return orderBetween(prevT, nextT, self);
}

/**
 * 같은 칼럼 안에서 task 에 newSortOrder 를 적용해도 정렬 순서가 그대로면 true(=제자리 드롭, no-op).
 * sortOrder=null 인 기본 정렬 카드는 taskSortValue 가 큰 기본 상수라 newSortOrder(이웃 기준 분수값)와
 * 직접 비교하면 제자리 드롭도 변경으로 오인하므로, 값이 아니라 "재정렬 후 uid 순서"로 판정한다(JCG-2310 리뷰).
 * columnTasks 는 그 칼럼의 현재 정렬된 task(자기 포함).
 */
export function isUnchangedColumnOrder(
  task: ScheduledTask,
  newSortOrder: number,
  columnTasks: ScheduledTask[],
): boolean {
  const current = columnTasks.map((t) => t.uid).join("\n");
  const next = columnTasks
    .map((t) => ({
      uid: t.uid,
      v: t.uid === task.uid ? newSortOrder : taskSortValue(t),
    }))
    .sort((a, b) => a.v - b.v)
    .map((x) => x.uid)
    .join("\n");
  return current === next;
}

/** dropIndicator 를 이 칼럼의 렌더 task 기준 삽입 인덱스(0..n)로. 이 칼럼이 대상이 아니면 null. */
export function dropInsertIndex(
  tasks: ScheduledTask[],
  dropIndicator: DropIndicator | null,
  dateISO: string,
): number | null {
  if (!dropIndicator || dropIndicator.toISO !== dateISO) return null;
  if (dropIndicator.refUid === null) return tasks.length;
  const i = tasks.findIndex((t) => t.uid === dropIndicator.refUid);
  if (i < 0) return tasks.length;
  return dropIndicator.after ? i + 1 : i;
}

/**
 * 떠 있는 드래그 클론 — body 로 portal + position:fixed(조상 transform·스크롤 클리핑 회피).
 * 시작 위치/폭은 rect 로 고정하고, 이동은 호출부가 overlayRef.current.style.transform 으로 e.delta
 * 만큼 직접 갱신한다(re-render 없이). portal 은 DndContext 안에서 렌더해야 클론 TaskCard 의
 * useDraggable 컨텍스트가 유지된다(주 뷰·모달 공용).
 */
export function DragClone({
  rect,
  overlayRef,
  children,
}: {
  rect: { left: number; top: number; width: number };
  overlayRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={overlayRef}
      className="pointer-events-none fixed z-[60]"
      style={{ left: rect.left, top: rect.top, width: rect.width }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** 드롭 삽입 인디케이터 — amber 점+라인. 위아래 틈을 벌려 그 자리에 일정이 들어오며 아래가 밀려남을 보여준다. */
export function InsertionLine() {
  return (
    <div
      className="flex items-center gap-1 py-1 duration-150 animate-in fade-in zoom-in-95"
      aria-hidden
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500 dark:bg-amber-400" />
      <span className="h-0.5 flex-1 rounded-full bg-amber-500 dark:bg-amber-400" />
    </div>
  );
}

const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * 리스트 재정렬 시 카드가 새 위치로 미끄러지는 FLIP 애니메이션.
 *
 * containerRef 안 [data-flip-uid] 요소들의 위치를 orderKey 가 바뀔 때마다 측정해, 이전→현재 위치차를
 * transform 으로 되돌렸다가(0s) 다음 프레임에 0 으로 transition → 미끄러지는 효과.
 * - 드래그 중에는 orderKey 가 안 바뀌므로(인디케이터만 변함) 실행되지 않고, 드롭으로 순서가 바뀔 때만 돈다.
 * - excludeUidRef(직전 드래그해서 떨어뜨린 카드)는 슬라이드에서 제외 — 드롭 지점에 그대로 안착시킨다
 *   (원래 자리로 텔레포트했다 미끄러지는 어색함 방지). 1회성으로, 사용 후 ref 를 비운다.
 */
export function useFlipReorder(
  containerRef: RefObject<HTMLElement | null>,
  orderKey: string,
  opts?: {
    /** 직전 드롭 카드(슬라이드 제외). 소비자가 드래그 시작 시 null 로 비운다(1회성). */
    excludeUidRef?: RefObject<string | null>;
    /** 슬라이드 축. 카드 재정렬=세로(y, 기본), 칼럼 윈도우 이동=가로(x). */
    axis?: "x" | "y";
    /** 측정 마커 속성. 카드=data-flip-uid(기본), 칼럼=data-day-column. */
    attr?: string;
    /** false 면 애니메이트 없이 위치만 기록(예: 빠른 휠/스와이프엔 슬라이드 생략). */
    enabled?: boolean;
  },
): void {
  const excludeUidRef = opts?.excludeUidRef;
  const axis = opts?.axis ?? "y";
  const attr = opts?.attr ?? "data-flip-uid";
  const enabled = opts?.enabled ?? true;
  const prev = useRef<Map<string, number>>(new Map());
  useIsoLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const exclude = excludeUidRef?.current ?? null;
    const next = new Map<string, number>();
    for (const node of el.querySelectorAll<HTMLElement>(`[${attr}]`)) {
      const id = node.getAttribute(attr);
      if (!id) continue;
      const r = node.getBoundingClientRect();
      const pos = axis === "x" ? r.left : r.top;
      next.set(id, pos);
      const old = prev.current.get(id);
      if (enabled && old != null && old !== pos && id !== exclude) {
        node.style.transform =
          axis === "x"
            ? `translateX(${old - pos}px)`
            : `translateY(${old - pos}px)`;
        node.style.transition = "transform 0s";
        requestAnimationFrame(() => {
          node.style.transform = "";
          node.style.transition = "transform 220ms cubic-bezier(0.2, 0, 0, 1)";
        });
      }
    }
    prev.current = next;
  }, [containerRef, orderKey, excludeUidRef, axis, attr, enabled]);
}
